import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send, Bot } from "lucide-react";
import { API, PORTAL_URL } from "@/lib/site";

const sessionId = () => {
  let s = localStorage.getItem("regal_chat_session");
  if (!s) {
    s = crypto.randomUUID();
    localStorage.setItem("regal_chat_session", s);
  }
  return s;
};

const WELCOME = "Hello — I'm the Regal Tuning technical assistant. Ask me about tuning files, tools like KESS3 or AutoTuner, or becoming a dealer. To upload an ECU file, use the File Portal.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const { token } = JSON.parse(payload);
            if (token) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + token };
                return c;
              });
            }
          } catch { /* partial json */ }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { ...c[c.length - 1], content: "Sorry — the assistant is unavailable right now. Please try again or use the WhatsApp technical desk." };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[80] flex h-[520px] max-h-[70vh] w-[calc(100vw-2rem)] max-w-sm flex-col border border-linebright bg-card shadow-[0_24px_64px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center bg-regal-amber text-void"><Bot size={18} /></span>
                <div>
                  <p className="font-display text-sm font-bold uppercase tracking-[0.14em]">Regal AI Assistant</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-600">Online — technical desk</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close-button" className="grid h-9 w-9 place-items-center border border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors">
                <X size={16} />
              </button>
            </div>
            <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}-${i}`}
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-regal-amber text-void font-medium"
                        : "border border-line bg-surface text-neutral-200"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <span className="inline-block h-4 w-2 animate-pulse bg-regal-amber" /> : "")}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-line px-4 pt-2 pb-1">
              <p className="text-center font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-600">
                ECU file uploads? Use the{" "}
                <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-regal-amber hover:underline" data-testid="chat-portal-link">File Portal</a>
              </p>
            </div>
            <form onSubmit={send} className="flex items-center gap-2 border-t border-line p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about files, tools, dealers…"
                data-testid="chat-input"
                className="flex-1 border border-line bg-surface px-4 py-3 text-sm text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                data-testid="chat-send-button"
                className="grid h-11 w-11 shrink-0 place-items-center bg-regal-amber text-void hover:bg-amber-400 transition-colors disabled:opacity-40"
              >
                <Send size={17} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open AI assistant"
        data-testid="chat-widget-button"
        className="fixed bottom-5 right-4 sm:right-6 z-[80] flex h-14 items-center gap-2.5 border border-regal-amber/60 bg-card/95 px-5 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber shadow-[0_12px_32px_rgba(0,0,0,0.5)] backdrop-blur-md hover:bg-regal-amber hover:text-void transition-colors duration-300"
      >
        <MessageSquare size={17} />
        <span className="hidden sm:inline">{open ? "Close" : "Ask Regal AI"}</span>
      </button>
    </>
  );
}
